import * as FormSelectors from "ui-kit/Form/selectors";
import * as R from "ramda";
import { createSelector } from "reselect";
import * as PortalUserSelectors from "redux/modules/portal/user/selectors";
import * as PortalSelectors from "redux/modules/portal/selectors";

export const getEditedField = R.compose(
  field => R.prop("value", field),
  FormSelectors.getField
);

const getName = ({ user_lname, user_fname, user_email }) =>
  user_fname && user_lname ? `${user_fname} ${user_lname}` : user_email;

export const getActiveAccountId = createSelector(
  state => PortalUserSelectors.portalUser(state),
  portalUser => {
    const isViewingAsAccount = R.pathEq(
      ["active_view", "type"],
      "account"
    )(portalUser);
    const accountId = isViewingAsAccount
      ? R.path(["active_view", "id"], portalUser)
      : null;
    return accountId;
  }
);

export const getActiveContactId = createSelector(
  state => PortalUserSelectors.portalUser(state),
  portalUser => {
    const isViewingAsContact = R.pathEq(
      ["active_view", "type"],
      "user"
    )(portalUser);
    const contactId = isViewingAsContact
      ? R.path(["active_view", "contact_id"], portalUser)
      : null;
    return contactId;
  }
);

export const getActiveAccount = createSelector(
  state => PortalUserSelectors.portalUser(state),
  state => PortalUserSelectors.accounts(state),
  (portalUser, accounts) => {
    const isViewingAsAccount =
      R.path(["active_view", "type"], portalUser) !== "user";
    if (!isViewingAsAccount) {
      return null;
    }
    const acct = R.find(
      R.propEq("id", R.path(["active_view", "id"], portalUser)),
      accounts
    );
    if (acct) {
      return acct;
    }
    return null;
  }
);

export const getActiveContact = createSelector(
  state => PortalUserSelectors.portalUser(state),
  portalUser => {
    const isViewingAsContact =
      R.path(["active_view", "type"], portalUser) === "user";
    if (!isViewingAsContact) {
      return null;
    }
    return {
      name: R.path(["active_view", "contact_name"])(portalUser),
      email: R.path(["active_view", "contact_email"])(portalUser),
      title: R.path(["active_view", "contact_title"])(portalUser),
      company: R.path(["active_view", "contact_company"])(portalUser),
      description: R.path(["active_view", "contact_biography"])(portalUser),
      photo_url: R.path(["active_view", "contact_photo_url"])(portalUser)
    };
  }
);

export const getIsEventAdmin = createSelector(
  PortalUserSelectors.portalUser,
  portalUser => {
    return portalUser.is_event_user;
  }
);

// @NOTE: This is to be used just for the user name - not the contact name
export const getUserName = createSelector(
  state => PortalUserSelectors.portalUser(state),
  user => {
    return getName(user);
  }
);

// @NOTE: This is to be used just for the contact name - not the user name
export const getPortalUserName = createSelector(
  getActiveAccountId,
  getActiveContact,
  state => PortalUserSelectors.portalUser(state),
  (activeAccountId, activeContact, user) => {
    return activeAccountId
      ? getName(user)
      : activeContact
      ? activeContact.name
      : null;
  }
);

export const getProfilePanelTitle = createSelector(
  getActiveAccount,
  activeAccount => {
    return activeAccount
      ? `${activeAccount.name.replace(/s$/, "")} Profile`
      : "My Profile";
  }
);

export const getPortalUserTitle = createSelector(
  getActiveContact,
  activeContact => {
    return activeContact ? activeContact.title : null;
  }
);

export const getPortalUserCompany = createSelector(
  getActiveContact,
  activeContact => {
    return activeContact ? activeContact.company : null;
  }
);

export const getPortalUserDescription = createSelector(
  getActiveContact,
  activeContact => {
    return activeContact ? activeContact.description : null;
  }
);

export const getPortalUserEmail = createSelector(
  getActiveContact,
  activeContact => {
    return activeContact ? activeContact.email : null;
  }
);

export const getUserPhoto = createSelector(
  state => PortalUserSelectors.portalUser(state),
  ({ user_photo_url }) => user_photo_url || null
);

export const getPortalUserPhoto = createSelector(
  getActiveContact,
  activeContact => {
    return activeContact ? activeContact.photo_url : null;
  }
);

export const getPortalEventByOrg = createSelector(
  state => PortalSelectors.portal(state),
  ({ events = [] }) => {
    // the BE need to add the org of each event or group by org
    return events;
  }
);
